import { useEffect, useState } from 'react'
import { normalizeHex } from './colorUtils'
import './ColorField.css'

/**
 * A colour, as both a swatch and a hex value.
 *
 * The two halves are one control: the swatch opens the OS picker for choosing,
 * the text field is for pasting the hex a brand guideline actually specifies —
 * which is how these are set in practice, and something a bare `<input
 * type="color">` cannot do at all.
 *
 * The typed value is kept RAW while editing and only normalised on blur, so a
 * half-typed `#0e7` is not rewritten under the caret.
 *
 * @param {string} value  `#rrggbb`, or '' for "not set"
 * @param {(hex: string) => void} onChange  called with a normalised hex or ''
 * @param {string} [placeholder]  the hex used when the value is empty
 */
export default function ColorField({ id, value, onChange, placeholder, disabled }) {
  const [text, setText] = useState(value ?? '')

  useEffect(() => {
    setText(value ?? '')
  }, [value])

  const effective = normalizeHex(value) || normalizeHex(placeholder) || '#000000'

  const commit = (raw) => {
    const hex = normalizeHex(raw)
    // An unparseable entry falls back to "not set" rather than being kept as
    // text that looks saved but cannot be applied.
    setText(hex)
    onChange?.(hex)
  }

  return (
    <div className={`color-field${disabled ? ' is-disabled' : ''}`}>
      <label className="color-field__swatch" style={{ background: effective }}>
        <input
          type="color"
          value={effective}
          disabled={disabled}
          onChange={(event) => {
            setText(event.target.value)
            onChange?.(normalizeHex(event.target.value))
          }}
          aria-label={placeholder}
        />
      </label>

      <input
        id={id}
        className="color-field__hex"
        type="text"
        dir="ltr"
        spellCheck={false}
        autoComplete="off"
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => setText(event.target.value)}
        onBlur={(event) => commit(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            commit(event.currentTarget.value)
          }
        }}
      />
    </div>
  )
}
